import { WSCard, WSElement, WSText } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useQueryPayables } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import {
  PayablesFilters,
  PayablesStatusFilter,
  PayablesTable,
  usePayablesQuery
} from "../../components/NewPayablesTable/PayablesTable";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { API_QUERY_PAYABLES_TO_APPROVE } from "../../utils/payables/apiQueries";
import { useApproveAll } from "../../utils/payables/approveAll";
import { useApproveAllBulkAction } from "../../utils/payables/approveAllBulkAction";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

const DEFAULT_FILTERS: PayablesFilters = {
  status: [PayablesStatusFilter.ToApprove]
};

export const PayablesToApprove: React.FC = () => {
  useBrowserPageTitle("Payables To Approve");
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();
  const featureFlags = useFeatureFlags();

  const queryPayablesDraft = usePayablesQuery({
    status: [PayablesStatusFilter.Draft]
  });

  const queryPayablesToApprove = useQueryPayables({
    ...API_QUERY_PAYABLES_TO_APPROVE,
    page: {
      size: 1
    }
  });

  const legacyApproveAll = useApproveAll();
  const approveAllBulkAction = useApproveAllBulkAction();

  const isBulkActions = featureFlags.data?.bulkPayableActions;

  return (
    <>
      <WSText.Heading1 mb="M">To Approve</WSText.Heading1>
      <WSText color="gray600" mb="2XL">
        All payables and requests that are not yet scheduled to be paid.
      </WSText>

      <WSElement mb="2XL">
        <WSQueries
          queries={{ queryPayablesDraft, queryPayablesToApprove }}
          renderErrors={() => null}
        >
          {({
            queryPayablesDraft: {
              data: { summary: draftPayablesSummary }
            },
            queryPayablesToApprove: {
              data: { summary: toApprovePayablesSummary }
            }
          }) => (
            <>
              {toApprovePayablesSummary.listSize > 1 && (
                <WSCard
                  mb="XL"
                  header={{
                    label: {
                      avatar: {
                        type: "icon",
                        icon: "dollar-circle",
                        size: "M",
                        color: "amber400",
                        colorBackground: "amber50"
                      },
                      text: "Pending approval:"
                    }
                  }}
                  divider="bottom"
                  actions={{
                    alignment: "left",
                    buttons: [
                      {
                        label: `Approve ${toApprovePayablesSummary.listSize} payments`,
                        kind: "Link",
                        rightIcon: "chevron-right",
                        onClick: isBulkActions
                          ? () =>
                              approveAllBulkAction(
                                toApprovePayablesSummary.listSize
                              )
                          : legacyApproveAll.act,
                        loading: isBulkActions
                          ? undefined
                          : legacyApproveAll.isLoading
                      }
                    ]
                  }}
                >
                  <WSText.ParagraphSm color="gray500">
                    {toApprovePayablesSummary.listSize} payables totaling{" "}
                    {formatMoney(
                      toApprovePayablesSummary.totalValue,
                      defaultCurrency
                    )}
                  </WSText.ParagraphSm>
                </WSCard>
              )}

              {draftPayablesSummary.listSize > 0 && (
                <WSCard
                  header={{
                    label: {
                      avatar: {
                        type: "icon",
                        icon: "alert-circle",
                        size: "M",
                        color: "amber400",
                        colorBackground: "amber50"
                      },
                      text: "Draft payables"
                    }
                  }}
                  divider="bottom"
                  actions={{
                    alignment: "left",
                    buttons: [
                      {
                        label: "Manage draft payables",
                        kind: "Link",
                        rightIcon: "chevron-right",
                        onClick: () => {
                          history.push("/member/invoices/payables/draft");
                        }
                      }
                    ]
                  }}
                >
                  <WSText.ParagraphSm color="gray500">
                    {draftPayablesSummary.listSize} draft payable
                    {draftPayablesSummary.listSize > 1 ? "s" : ""}.
                  </WSText.ParagraphSm>
                </WSCard>
              )}
            </>
          )}
        </WSQueries>
      </WSElement>

      <PayablesTable
        bulkActions={["approve", "delete"]}
        visibleFilters={{
          payee: true,
          collaboratorReviewStatus: true,
          openedAtRange: true,
          dueDateRange: true
        }}
        basePath="/member/invoices/payables/to-approve"
        defaultFilters={DEFAULT_FILTERS}
        showHeaders
        showInvoiceNumber
        showDateRequested
        showDueDate
        emptyState={{
          firstTime:
            "Every requested, scheduled, and paid payable will display here.",
          standart:
            "You’re all caught up! Nothing is waiting for your approval."
        }}
      />
    </>
  );
};
